import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as core from '../../core'
/**
 * GL Expense type has MTDC category. There are three MTDC categories: Salary,
 * Fringe and Non-Salary. Fringe and Salary are considered for Labor and
 * Non-Salary is Non-Labor. In some reports we need to make calculations based
 * on Labor and Non-Labor
 *
 * TODO: better description
 */
export enum MtdcCategory {
  Salary = 'Salary',
  Fringe = 'Fringe',
  NonSalary = 'Non-Salary',
}
// Needs to be before namespace is defined
const MTDC_CATEGORIES: ReadonlySet<MtdcCategory> = new Set(
  Object.values(MtdcCategory) as MtdcCategory[],
)
export namespace MtdcCategory {
  export const ALL: readonly MtdcCategory[] = Array.from(MTDC_CATEGORIES)
  export const isMtdcCategory = TypeChecker.Factory.oneOf(MTDC_CATEGORIES)
  export const assertIsMtdcCategory: TypeAsserter<MtdcCategory> =
    TypeAsserter.Factory.from(isMtdcCategory)

  /**
   * Returns the {@link core.common.value.types.Enum.Entry} for the given
   * {@link MtdcCategory}
   */
  export function getEntry(
    mtdcCategory: MtdcCategory,
  ): core.common.value.types.Enum.Entry {
    return { ...ENTRY_LOOKUP[mtdcCategory], id: mtdcCategory }
  }
  /** Used by {@link getEntry} */
  const ENTRY_LOOKUP: Record<
    MtdcCategory,
    Omit<core.common.value.types.Enum.Entry, 'id'>
  > = {
    [MtdcCategory.Fringe]: {
      description:
        'Labor expenses that are not salary & wages. Often these correspond to benefits offered by the institution',
      name: 'Fringe Benefits',
    },
    [MtdcCategory.NonSalary]: {
      description: 'Expenses that are unrelated to labor',
      name: 'Non-Salary',
    },
    [MtdcCategory.Salary]: {
      description: 'Employee salary expenses',
      name: 'Salary & Wages',
    },
  }

  type ForLaborList = readonly [MtdcCategory.Fringe, MtdcCategory.Salary]
  /**
   * An {@link MtdcCategory} that is associated with labor-related expenses
   */
  export type ForLabor = ForLaborList[number]
  export namespace ForLabor {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export const ALL: readonly ForLabor[] = [
      MtdcCategory.Fringe,
      MtdcCategory.Salary,
    ] satisfies ForLaborList
    export const isForLabor: TypeChecker<ForLabor> =
      TypeChecker.Factory.oneOf(ALL)
  }

  /**
   * Any {@link MtdcCategory} that is not {@link ForLabor}
   */
  export type ForNonLabor = Exclude<MtdcCategory, ForLabor>
  export namespace ForNonLabor {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export const ALL: readonly ForNonLabor[] = MtdcCategory.ALL.filter(
      (mtdcCategory): mtdcCategory is ForNonLabor =>
        !ForLabor.isForLabor(mtdcCategory),
    )
    export const isForNonLabor: TypeChecker<ForNonLabor> =
      TypeChecker.Factory.oneOf(ALL)
  }
}
